import { useMemo } from "react";
import useSWR, { mutate } from "swr";
import { type DocumentReleaseSummary } from "../types";
import { simpleFetcher } from "../fetcher";
import { type DocumentArtifactStatus } from "~/config/types";
import { includesSearch } from "~/helpers/string";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";

export const QueryParams = {
  Title: "searchTitle",
};

const url = "/api/v2/document-releases";
const workflowDataUrl = `${url}?includeWorkflowData=true`;

interface Params {
  statuses?: DocumentArtifactStatus[];
  titleSearch?: string | null;
  documentId?: number;
  tagIds?: number[];
  categoryIds?: number[];
  publishedBefore?: Date;
  includeWorkflowData?: boolean;
}

export function useDocumentReleases({
  statuses,
  titleSearch,
  documentId,
  tagIds,
  categoryIds,
  publishedBefore,
  includeWorkflowData,
}: Params = {}) {
  const isRefNumEnabled = useFeatureFlagEnabled(featureFlags.referenceNumber);
  const fetchUrl = includeWorkflowData ? workflowDataUrl : url;
  const swr = useSWR<DocumentReleaseSummary[], Error>(fetchUrl, simpleFetcher);

  const filteredData = useMemo(
    () =>
      swr.data?.filter(
        (release) =>
          (!statuses || statuses.includes(release.artifact.status)) &&
          (!titleSearch ||
            includesSearch(release.artifact.title, titleSearch) ||
            (isRefNumEnabled &&
              includesSearch(
                release.document.referenceNumber ?? "",
                titleSearch,
              ))) &&
          (!documentId || release.document.id === documentId) &&
          (!tagIds?.length ||
            tagIds.some((tagId) => release.document.tagIds.includes(tagId))) &&
          (!categoryIds?.length ||
            categoryIds.some((dId) => release.document.category.id === dId)) &&
          (!publishedBefore ||
            (release.artifact.publishedAt &&
              new Date(release.artifact.publishedAt) < publishedBefore)),
      ),
    [
      swr.data,
      statuses,
      titleSearch,
      isRefNumEnabled,
      documentId,
      tagIds,
      categoryIds,
      publishedBefore,
    ],
  );

  return {
    data: filteredData,
    error: swr.error,
    mutate: swr.mutate,
    isLoading: swr.isLoading,
  };
}

export const mutateDocumentReleases = () =>
  Promise.all([mutate(url), mutate(workflowDataUrl)]);
